import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import api from "@/api/client";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [offline, setOffline] = useState(false);

  useEffect(() => {
    // Session-Check: httpOnly-Cookie wird automatisch mitgesendet
    api
      .get("/auth/me", { _skipAuthRedirect: true })
      .then((res) => {
        setUser(res.data);
        setOffline(false);
        localStorage.setItem("bc_logged_in", "1");
        // Nicht-sensibles Profil zwischenspeichern (kein Token!) für Offline-Nutzung
        const { id, email, role, name, vorname, nachname } = res.data || {};
        localStorage.setItem("bc_user", JSON.stringify({ id, email, role, name, vorname, nachname }));
      })
      .catch((err) => {
        // Kein HTTP-Response => Netzwerk/Offline: Session NICHT verwerfen,
        // sondern zwischengespeichertes Profil weiterverwenden (Feldeinsatz ohne Internet).
        const isNetworkError = !err?.response;
        const cached = localStorage.getItem("bc_user");
        if (isNetworkError && localStorage.getItem("bc_logged_in") === "1" && cached) {
          try {
            setUser(JSON.parse(cached));
            setOffline(true);
            return;
          } catch (parseErr) {
            console.warn("Offline-Profil unlesbar:", parseErr);
          }
        }
        // Echte Abmeldung (401/403) -> Banner "Sitzung abgelaufen"
        if (localStorage.getItem("bc_logged_in") === "1") {
          sessionStorage.setItem("bc_session_expired", "1");
        }
        localStorage.removeItem("bc_logged_in");
        localStorage.removeItem("bc_user");
      })
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, password) => {
    const res = await api.post("/auth/login", { email, password });
    // Token liegt im sicheren httpOnly-Cookie (niemals im localStorage)
    localStorage.setItem("bc_logged_in", "1");
    const me = await api.get("/auth/me");
    setUser(me.data);
    setOffline(false);
    const { id, role, name, vorname, nachname } = me.data || {};
    localStorage.setItem("bc_user", JSON.stringify({ id, email: me.data?.email, role, name, vorname, nachname }));
    return me.data;
  };

  const logout = () => {
    api.post("/auth/logout").catch(() => {});
    localStorage.removeItem("bc_logged_in");
    localStorage.removeItem("bc_user");
    setUser(null);
  };

  const value = useMemo(() => ({ user, login, logout, loading, offline }), [user, loading, offline]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}

export function hasRole(user, roles) {
  if (!user) return false;
  return roles.includes(user.role);
}
